/**
 * PortfolioOptimizationSuggestions - AI-Powered Portfolio Optimization
 * TASK-206 Phase 3: Advanced Features
 *
 * Provides AI-driven rebalancing recommendations, concentration reduction,
 * and cash deployment strategies based on client's complete portfolio picture
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lightbulb, TrendingUp, AlertCircle, AlertTriangle, DollarSign, CheckCircle2,
         ChevronDown, ChevronUp, HelpCircle, Loader2 } from 'lucide-react';
import Badge from './atoms/Badge';

export interface OptimizationScenario {
  id: string;
  type: 'CONCENTRATION' | 'DUPLICATE' | 'CASH_DEPLOYMENT' | 'REBALANCING' | 'TAX_EFFICIENCY';
  priority: 'HIGH' | 'MEDIUM' | 'LOW';
  title: string;
  description: string;
  currentState: string;
  targetState: string;
  estimatedImpact: {
    riskReduction?: number; // Percentage points
    returnImprovement?: number; // Expected % return improvement
    taxSavings?: number; // CHF amount
    efficiencyGain?: number; // Sharpe ratio improvement
  };
  actions: OptimizationAction[];
  rationale: string;
}

export interface OptimizationAction {
  id: string;
  type: 'SELL' | 'BUY' | 'TRANSFER' | 'REBALANCE';
  security: string;
  from?: string; // Portfolio name
  to?: string; // Portfolio name or "Market"
  amount: number; // CHF
  percentage: number; // % of total
  reason: string;
}

/**
 * TASK-722. Absence of analysis is not evidence of health.
 *
 * An empty `scenarios` array means one of several very different things, and the RM has to
 * be able to tell them apart:
 *   complete     — the analysis ran, succeeded, and found nothing. Only this may be positive.
 *   loading      — it is running now.
 *   failed       — it ran and errored.
 *   incomplete   — it ran but could not cover the whole portfolio.
 *   unavailable  — it has not run, or the caller cannot prove that it did.
 */
export type OptimizationAnalysisStatus =
  | 'complete'
  | 'loading'
  | 'failed'
  | 'incomplete'
  | 'unavailable';

export interface PortfolioOptimizationSuggestionsProps {
  clientId: string;
  scenarios: OptimizationScenario[];
  totalAuM: number;
  className?: string;
  /**
   * Defaults to 'unavailable' ON PURPOSE. A caller that cannot prove the analysis completed
   * must not get a positive verdict by omission — which is exactly how this component came
   * to tell every client, always, that their portfolio was well optimized: the only caller
   * passed `data.optimizationScenarios ?? []`, and that field is assigned by no backend.
   */
  analysisStatus?: OptimizationAnalysisStatus;
  /** Optional detail for the failed/incomplete states. Shown verbatim to the RM. */
  analysisNote?: string;
}

const PortfolioOptimizationSuggestions: React.FC<PortfolioOptimizationSuggestionsProps> = ({
  clientId: _clientId,
  scenarios,
  totalAuM,
  className = '',
  analysisStatus = 'unavailable',
  analysisNote
}) => {
  // TASK-756: every string below resolves through the translation layer, with the English as
  // each key's default value. The five honest states keep FIVE SEPARATE keys on purpose —
  // collapsing them into one message in any language is exactly how they re-collapse in the
  // code, and "not assessed" would start reading as "fine" again.
  const { t } = useTranslation('portfolio');
  const [expandedScenario, setExpandedScenario] = useState<string | null>(null);

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `CHF ${(value / 1000000).toFixed(2)}M`;
    } else if (value >= 1000) {
      return `CHF ${(value / 1000).toFixed(0)}K`;
    }
    return `CHF ${value.toLocaleString('de-CH')}`;
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case 'HIGH':
        return <Badge variant="error" size="sm" label={t('optimization.priority.high', 'HIGH PRIORITY')} />;
      case 'MEDIUM':
        return <Badge variant="warning" size="sm" label={t('optimization.priority.medium', 'MEDIUM')} />;
      case 'LOW':
        return <Badge variant="default" size="sm" label={t('optimization.priority.low', 'LOW')} />;
      default:
        return null;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'CONCENTRATION':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      case 'DUPLICATE':
        return <AlertCircle className="w-5 h-5 text-yellow-600" />;
      case 'CASH_DEPLOYMENT':
        return <DollarSign className="w-5 h-5 text-blue-600" />;
      case 'REBALANCING':
        return <TrendingUp className="w-5 h-5 text-green-600" />;
      case 'TAX_EFFICIENCY':
        return <Lightbulb className="w-5 h-5 text-purple-600" />;
      default:
        return <Lightbulb className="w-5 h-5 text-gray-600" />;
    }
  };

  const getActionBadge = (type: string) => {
    switch (type) {
      case 'SELL':
        return <Badge variant="error" size="sm" label={t('optimization.action.sell', 'SELL')} />;
      case 'BUY':
        return <Badge variant="success" size="sm" label={t('optimization.action.buy', 'BUY')} />;
      case 'TRANSFER':
        return <Badge variant="default" size="sm" label={t('optimization.action.transfer', 'TRANSFER')} />;
      case 'REBALANCE':
        return <Badge variant="warning" size="sm" label={t('optimization.action.rebalance', 'REBALANCE')} />;
      default:
        return null;
    }
  };

  const toggleScenario = (scenarioId: string) => {
    setExpandedScenario(expandedScenario === scenarioId ? null : scenarioId);
  };

  // TASK-277: Add null safety for scenarios prop
  const safeScenarios = scenarios ?? [];

  // TASK-872: the subtitle must agree with the panel below it, in all five states.
  //
  // "0 optimization opportunities identified" rendered unconditionally while the empty-state
  // panel twenty lines down said "No optimization analysis has run for this client, so nothing
  // can be said about whether the portfolio is well optimized." Both on screen at once: one
  // says the analysis looked and found nothing, the other says it never looked.
  //
  // Two corrections were needed to get here, both caught by tests rather than by reasoning:
  //   • keying on `length === 0` swallowed the honest zero — a COMPLETED analysis that found
  //     nothing has a finding, and "0 identified" is exactly right (the i18n suite);
  //   • keying on `complete` vs everything else made `loading`, `failed` and `incomplete` all
  //     claim the analysis never ran, while the panel below correctly said it was running or
  //     had errored — the same contradiction, in three more states (review).
  //
  // A count is a FINDING, so it is stated only where findings exist. Every other state is
  // carried by the panel below — which already distinguishes all five — and repeating it here
  // would put the same sentence on screen twice.
  const hasFindingsToCount =
    analysisStatus === 'complete' ||
    // Partial findings are still findings; the panel says they are partial.
    (analysisStatus === 'incomplete' && safeScenarios.length > 0);

  const headerSubtitle = hasFindingsToCount
    ? t('optimization.opportunities', {
        count: safeScenarios.length,
        defaultValue: `${safeScenarios.length} optimization opportunities identified`,
      })
    : null;

  // Sort scenarios by priority
  const sortedScenarios = [...safeScenarios].sort((a, b) => {
    const priorityOrder = { HIGH: 0, MEDIUM: 1, LOW: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Lightbulb className="w-6 h-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t('optimization.title', 'AI Portfolio Optimization')}
            </h3>
            {/* TASK-872: the count is a FINDING, and only a completed analysis has findings.
                "0 optimization opportunities identified" rendered here while the panel twenty
                lines below said "No optimization analysis has run for this client, so nothing
                can be said about whether the portfolio is well optimized." Both on screen at
                once: one says the analysis looked and found nothing, the other says it never
                looked.

                Keyed on `analysisStatus`, NOT on the count — a first cut keyed on
                `length === 0` and broke the honest case, where a COMPLETED analysis genuinely
                found zero opportunities and "0 identified" is exactly right. The i18n suite
                caught it. Absence and zero are different facts here too. */}
            {headerSubtitle && (
              <p className="text-sm text-gray-600">{headerSubtitle}</p>
            )}
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-gray-900">
            {formatCurrency(totalAuM)}
          </div>
          <p className="text-xs text-gray-500">{t('optimization.totalAum', 'Total AuM')}</p>
        </div>
      </div>

      {/* Optimization Scenarios */}
      <div className="space-y-4">
        {sortedScenarios.map((scenario) => {
          const isExpanded = expandedScenario === scenario.id;

          return (
            <div
              key={scenario.id}
              className={`bg-white rounded-lg border-2 transition-all ${
                scenario.priority === 'HIGH'
                  ? 'border-red-300 shadow-md'
                  : scenario.priority === 'MEDIUM'
                  ? 'border-yellow-300'
                  : 'border-gray-200'
              }`}
            >
              {/* Scenario Header - Clickable */}
              <button
                onClick={() => toggleScenario(scenario.id)}
                className="w-full px-6 py-4 flex items-start justify-between hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start gap-4 flex-1">
                  {/* Icon */}
                  <div className="mt-1">
                    {getTypeIcon(scenario.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-base font-semibold text-gray-900">
                        {scenario.title}
                      </h4>
                      {getPriorityBadge(scenario.priority)}
                    </div>
                    <p className="text-sm text-gray-600 mb-3">
                      {scenario.description}
                    </p>

                    {/* Current vs Target State */}
                    <div className="grid grid-cols-2 gap-4 text-xs">
                      <div>
                        <span className="text-gray-500">{t('optimization.currentState', 'Current State:')}</span>
                        <p className="font-medium text-gray-900 mt-1">{scenario.currentState}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">{t('optimization.targetState', 'Target State:')}</span>
                        <p className="font-medium text-green-700 mt-1">{scenario.targetState}</p>
                      </div>
                    </div>

                    {/* Estimated Impact Badges.
                        TASK-872: optional-chained. A scenario without `estimatedImpact` threw
                        "Cannot read properties of undefined" and took the whole panel down —
                        found while writing this ticket's tests. A missing impact is a missing
                        badge, not a blank screen. */}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {scenario.estimatedImpact?.riskReduction && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-medium">
                          <TrendingUp className="w-3 h-3" />
                          {t('optimization.impact.risk', {
                            value: scenario.estimatedImpact?.riskReduction,
                            defaultValue: `-${scenario.estimatedImpact?.riskReduction}% Risk`,
                          })}
                        </span>
                      )}
                      {scenario.estimatedImpact?.returnImprovement && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                          <TrendingUp className="w-3 h-3" />
                          {t('optimization.impact.return', {
                            value: scenario.estimatedImpact?.returnImprovement,
                            defaultValue: `+${scenario.estimatedImpact?.returnImprovement}% Return`,
                          })}
                        </span>
                      )}
                      {scenario.estimatedImpact?.taxSavings && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs font-medium">
                          <DollarSign className="w-3 h-3" />
                          {t('optimization.impact.tax', {
                            amount: formatCurrency(scenario.estimatedImpact?.taxSavings),
                            defaultValue: `${formatCurrency(scenario.estimatedImpact?.taxSavings)} Tax Savings`,
                          })}
                        </span>
                      )}
                      {scenario.estimatedImpact?.efficiencyGain && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs font-medium">
                          <CheckCircle2 className="w-3 h-3" />
                          {t('optimization.impact.sharpe', {
                            value: scenario.estimatedImpact?.efficiencyGain.toFixed(2),
                            defaultValue: `+${scenario.estimatedImpact?.efficiencyGain.toFixed(2)} Sharpe`,
                          })}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Expand/Collapse Icon */}
                <div className="ml-4">
                  {isExpanded ? (
                    <ChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </button>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-6 pb-6 border-t border-gray-200">
                  {/* Rationale */}
                  <div className="mt-4 mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h5 className="text-sm font-semibold text-blue-900 mb-2">
                      {t('optimization.rationaleHeading', 'AI Analysis & Rationale')}
                    </h5>
                    <p className="text-sm text-blue-800">{scenario.rationale}</p>
                  </div>

                  {/* Action Steps */}
                  <h5 className="text-sm font-semibold text-gray-900 mb-3">
                    {t('optimization.actionsHeading', {
                      count: scenario.actions.length,
                      defaultValue: `Recommended Actions (${scenario.actions.length})`,
                    })}
                  </h5>
                  <div className="space-y-3">
                    {scenario.actions.map((action, idx) => (
                      <div
                        key={action.id}
                        className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        {/* Step Number */}
                        <div className="flex-shrink-0 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                          {idx + 1}
                        </div>

                        {/* Action Details */}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {getActionBadge(action.type)}
                            <span className="font-semibold text-gray-900">{action.security}</span>
                          </div>

                          <div className="grid grid-cols-2 gap-3 text-sm mb-2">
                            {action.from && (
                              <div>
                                <span className="text-gray-500">{t('optimization.from', 'From:')}</span>
                                <span className="ml-2 font-medium text-gray-900">{action.from}</span>
                              </div>
                            )}
                            {action.to && (
                              <div>
                                <span className="text-gray-500">{t('optimization.to', 'To:')}</span>
                                <span className="ml-2 font-medium text-gray-900">{action.to}</span>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-4 text-sm mb-2">
                            <div>
                              <span className="text-gray-500">{t('optimization.amount', 'Amount:')}</span>
                              <span className="ml-2 font-bold text-gray-900">{formatCurrency(action.amount)}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">{t('optimization.percentOfPortfolio', '% of Portfolio:')}</span>
                              <span className="ml-2 font-bold text-gray-900">{action.percentage.toFixed(1)}%</span>
                            </div>
                          </div>

                          <p className="text-xs text-gray-600 italic">
                            → {action.reason}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Implementation Note */}
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs text-yellow-800">
                      <strong>{t('optimization.note.label', 'Note:')}</strong>{' '}
                      {t(
                        'optimization.note.body',
                        "These are AI-generated suggestions based on modern portfolio theory and your client's risk profile. Always review with the client before executing any trades.",
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Empty states — five distinct outcomes, never collapsed into one green tick. */}
      {safeScenarios.length === 0 && (
        analysisStatus === 'complete' ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-3" />
            <h4 className="text-lg font-semibold text-green-900 mb-2">
              {t('optimization.states.complete.title', 'Portfolio is Well Optimized')}
            </h4>
            <p className="text-sm text-green-700">
              {t(
                'optimization.states.complete.body',
                'The optimization analysis completed and found no major opportunities. The portfolio allocation appears balanced.',
              )}
            </p>
          </div>
        ) : analysisStatus === 'loading' ? (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <Loader2 className="w-12 h-12 text-gray-400 mx-auto mb-3 animate-spin" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              {t('optimization.states.loading.title', 'Analysing the portfolio…')}
            </h4>
            <p className="text-sm text-gray-600">
              {t(
                'optimization.states.loading.body',
                'Optimization opportunities will appear here when the analysis finishes.',
              )}
            </p>
          </div>
        ) : analysisStatus === 'failed' ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
            <h4 className="text-lg font-semibold text-red-900 mb-2">
              {t('optimization.states.failed.title', 'The optimization analysis could not be completed')}
            </h4>
            <p className="text-sm text-red-700">
              {/* A server-supplied note is DATA and is shown verbatim; only the fallback
                  sentence is ours to translate. */}
              {analysisNote ||
                t(
                  'optimization.states.failed.body',
                  'This is not a statement about the portfolio — it means the analysis failed to run.',
                )}
            </p>
          </div>
        ) : analysisStatus === 'incomplete' ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h4 className="text-lg font-semibold text-amber-900 mb-2">
              {t('optimization.states.incomplete.title', 'The optimization analysis is incomplete')}
            </h4>
            <p className="text-sm text-amber-700">
              {analysisNote ||
                t(
                  'optimization.states.incomplete.body',
                  'Part of the portfolio could not be assessed, so no conclusion can be drawn.',
                )}
            </p>
          </div>
        ) : (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
            <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2">
              {t('optimization.states.unavailable.title', 'Optimization analysis is not available')}
            </h4>
            <p className="text-sm text-gray-600">
              {t(
                'optimization.states.unavailable.body',
                'No optimization analysis has run for this client, so nothing can be said about whether the portfolio is well optimized.',
              )}
            </p>
          </div>
        )
      )}
    </div>
  );
};

export default PortfolioOptimizationSuggestions;
